/**
 * AVA-475 — Verification point math.
 *
 * Pure, side-effect-free helpers behind every verification result. Nothing here
 * is stored: the Vuex layer keeps only raw design/as-built points plus an
 * optional offset, and re-derives adjusted points, deltas and verdicts on read.
 *
 * DOMAIN RULES (non-negotiable — see .claude/features/AVA-475_HPC_REPORTING_DEMO.md)
 * - World frame only. No UCS, no rotation.
 * - Per-axis vectors only (dX/dY/dZ). Never a combined 3D distance.
 * - Deltas are SIGNED: d = asBuiltAdjusted − designAdjusted.
 * - Tolerance boundary is INCLUSIVE: |d| <= tol PASSES.
 * - A point passes only when all three axes pass.
 * - Incomplete input yields a null verdict (Pending), never a false FAIL.
 * - TWO SPACES: positions arrive in VIEWER units (utils/modelUnits.js); deltas
 *   and tolerances are MILLIMETRES. `computeDeltas` is the crossing, and it is
 *   GIVEN the scale rather than assuming one.
 */

/**
 * Adjusted point = raw point + optional offset vector. Returns a NEW object
 * (never mutates the input — results are recomputed from source on every read).
 */
export function adjustPoint(point, offset) {
  if (!point) return null;
  if (!offset) return { ...point };
  return { x: point.x + offset.x, y: point.y + offset.y, z: point.z + offset.z };
}

/**
 * Millimetres in a metre. NO LONGER A UNIT BOUNDARY — kept only for the
 * genuinely metre-denominated maths, and no runtime caller remains.
 *
 * It used to be this feature's single metres -> millimetres conversion, on the
 * belief that a viewer coordinate WAS a metre. That belief was true of every
 * facility the feature was built against and false on `Master Test Site /
 * Merchant`, whose Forge model is authored in millimetres — where it turned a
 * real 3 mm deviation into 3000 mm and failed every check. Positions are now
 * declared to be in VIEWER units, whatever the model says those are, and the
 * scale that turns them into millimetres is `unitTransform.mmPerViewerUnit`,
 * resolved once from the model and threaded to each consumer.
 *
 * See `utils/modelUnits.js` for the full contract. Do not reintroduce this
 * constant as a conversion: a fixed 1000 is precisely the bug.
 */
export const METRES_TO_MM = 1000;

/**
 * DISPLAY PRECISION — both halves of one rule, so they cannot drift apart.
 *
 * The report states deviations in whole-and-tenth millimetres. A position must
 * therefore resolve FINER than the deviation derived from it, or two positions
 * that render identically can carry a deviation the reader cannot account for.
 * One decimal finer is the rule: 4 dp of a metre is 0.1 mm, which is exactly
 * the resolution `DEVIATION_DECIMALS` expresses in the other unit.
 *
 * These are DISPLAY constants only. Stored values keep whatever precision the
 * surveyor entered or the pick produced — see the note on `OFFSET_EPSILON_M`
 * for the one place where display precision feeds back into logic.
 */
export const POSITION_DECIMALS = 4;
export const DEVIATION_DECIMALS = 1;

/**
 * Display steps per millimetre. The feature resolves a position to a tenth of a
 * millimetre — the rule `POSITION_DECIMALS` expresses for a value that happens
 * to be in metres, stated in a form that survives a facility whose unit is not.
 */
const POSITION_STEPS_PER_MM = 10;

/**
 * How many decimals a VIEWER-UNIT coordinate needs to resolve 0.1 mm.
 *
 * `POSITION_DECIMALS` answers this for metres and only for metres. The wizard's
 * entry fields hold VIEWER units, which are millimetres on Merchant — so a
 * design pick pre-filled at 4 dp reads `25190.0078` there and claims a
 * ten-thousandth of a millimetre. That is noise wearing precision's clothes, and
 * a surveyor reading it has no way to tell the two apart. One decimal is the
 * honest answer on that facility; four is still the honest answer on a metre one.
 *
 *   1000 mm per unit (metres)      -> 4   (identical to POSITION_DECIMALS)
 *     10 mm per unit (centimetres) -> 2
 *      1 mm per unit (millimetres) -> 1
 *
 * NULL FOR AN UNKNOWN SCALE, never a default. A resolution that cannot be
 * derived must not be asserted: the caller shows the value as it is rather than
 * rounding it to a precision nothing supports.
 *
 * @param {number|null} mmPerViewerUnit From `unitTransform`.
 * @returns {number|null} Decimal places, or null when the scale is unknown.
 */
export function positionDecimalsFor(mmPerViewerUnit) {
  if (!Number.isFinite(mmPerViewerUnit) || mmPerViewerUnit <= 0) return null;
  // Multiply rather than divide by 0.1: `1000 / 0.1` is not exact in binary and
  // can land at 10000.000000000002, which ceils to a fifth decimal on a metre
  // model. `1000 * 10` is exact.
  return Math.max(0, Math.ceil(Math.log10(mmPerViewerUnit * POSITION_STEPS_PER_MM)));
}

/**
 * Absorbs binary floating-point representation error on the INCLUSIVE tolerance
 * boundary, in MILLIMETRES.
 *
 * Positions are metres and the delta is scaled by 1000, so a value the surveyor
 * typed as exactly at tolerance does not arrive as exactly at tolerance:
 * `(589.1030 - 589.1000) * 1000` is 2.9999999999745341, and the mirror case
 * lands at 3.0000000000001137, which a bare `<= 3` REFUSES. That is a FAIL on a
 * conformance document for no physical reason — and 4-decimal entry makes
 * typing a boundary value deliberate rather than accidental.
 *
 * 1e-6 mm is a nanometre: far below anything this feature can measure, resolve
 * or report, so it cannot mask a real violation. Same constant, same reasoning
 * and same shape as `checkAgainstLocks` in axisLock.js — keep them in step.
 */
export const FLOAT_EPSILON_MM = 1e-6;

/**
 * Signed per-axis deltas: as-built minus design.
 *
 * Inputs are VIEWER-unit points — metres on some facilities, millimetres on
 * others. `mmPerViewerUnit` is what makes the result millimetres either way, and
 * SCALE ALONE IS CORRECT HERE: a delta is a difference, so the model's
 * `globalOffset` cancels and only positions need `viewerToSite`.
 *
 * The third argument is REQUIRED, deliberately. A default of 1000 would restore
 * the original bug wearing a parameter's clothes — silently right on a metre
 * facility and silently 1000x wrong on a millimetre one. An absent or
 * nonsensical scale yields NULL DELTAS, the same shape `evaluatePoint(null, …)`
 * already handles, which renders as pending rather than as a wrong number.
 *
 * Never a combined 3D distance.
 *
 * @param {{x,y,z}|null} designAdjusted   VIEWER units.
 * @param {{x,y,z}|null} asBuiltAdjusted  VIEWER units.
 * @param {number|null} mmPerViewerUnit   From `unitTransform`; no default.
 * @returns {{dX:number, dY:number, dZ:number}|null} Millimetres, or null.
 */
export function computeDeltas(designAdjusted, asBuiltAdjusted, mmPerViewerUnit) {
  if (!designAdjusted || !asBuiltAdjusted) return null;
  if (!Number.isFinite(mmPerViewerUnit) || mmPerViewerUnit <= 0) return null;
  return {
    dX: (asBuiltAdjusted.x - designAdjusted.x) * mmPerViewerUnit,
    dY: (asBuiltAdjusted.y - designAdjusted.y) * mmPerViewerUnit,
    dZ: (asBuiltAdjusted.z - designAdjusted.z) * mmPerViewerUnit,
  };
}

/**
 * Per-axis pass/fail. Boundary is INCLUSIVE: |d| <= tol passes.
 * A point passes only when all three axes pass.
 */
export function evaluatePoint(deltas, tolerance) {
  if (!deltas || !tolerance) {
    return { passX: null, passY: null, passZ: null, pass: null };
  }
  // `|d| - tol <= EPSILON` rather than `|d| <= tol`: without it the INCLUSIVE
  // boundary is not actually inclusive. See FLOAT_EPSILON_MM. Do NOT "simplify"
  // this back to a bare `<=` — it reads like noise and it is the difference
  // between PASS and FAIL on a value typed as exactly at tolerance.
  const passX = Math.abs(deltas.dX) - tolerance.x <= FLOAT_EPSILON_MM;
  const passY = Math.abs(deltas.dY) - tolerance.y <= FLOAT_EPSILON_MM;
  const passZ = Math.abs(deltas.dZ) - tolerance.z <= FLOAT_EPSILON_MM;
  return { passX, passY, passZ, pass: passX && passY && passZ };
}

/**
 * Smallest offset that counts as one, in METRES.
 *
 * Offsets are stored in metres and rendered to POSITION_DECIMALS (4), so
 * anything below a twentieth of a millimetre formats as `+0.0000` — an offset
 * that claims to exist and displays as nothing. On a conformance document that
 * is worse than silence: the reader sees a stated adjustment with no value.
 *
 * Rounding to 4 dp IS the threshold, so it is defined once here and used by
 * every consumer — `showOffsetCheck`, the offset-check panel and the report.
 * Two different "is there an offset?" tests is how the panel and the report end
 * up disagreeing about whether one exists.
 *
 * THIS VALUE IS COUPLED TO `POSITION_DECIMALS` AND MUST MOVE WITH IT. It is
 * half of the last rendered digit. Leave it at the old 0.0005 while positions
 * render 4 dp and the invariant INVERTS: a 0.0003 m offset becomes plainly
 * visible on screen while `isSignificantOffsetAxis` still calls it nothing, so
 * the report silently omits an adjustment the surveyor can read off the panel.
 */
export const OFFSET_EPSILON_M = 0.00005;

/** True when a single axis carries an offset that will actually render. */
export function isSignificantOffsetAxis(value) {
  return Number.isFinite(value) && Math.abs(value) >= OFFSET_EPSILON_M;
}

/** True when any axis of an offset vector is significant. */
export function hasSignificantOffset(offset) {
  if (!offset) return false;
  return (
    isSignificantOffsetAxis(offset.x)
    || isSignificantOffsetAxis(offset.y)
    || isSignificantOffsetAxis(offset.z)
  );
}

/**
 * Metre-scale position or offset, rendered to POSITION_DECIMALS.
 *
 * THE ONE metre formatter in the feature. It used to be four: the report, the
 * result card, the offset-check panel and the wizard each carried their own
 * `toFixed(3)`, which is how a report ends up disagreeing with the screen it
 * was generated from.
 *
 * Two flags, because the four call sites genuinely differ:
 *
 *   `ascii` — THE SIGN GLYPH, and it is not cosmetic. The DOM wants U+2212
 *   MINUS SIGN (correct typography, and it matches `formatDelta` so signs do
 *   not jitter column widths). jsPDF's built-in Helvetica encodes as WinAnsi
 *   (cp1252), which has NO U+2212: it drops the character SILENTLY, printing
 *   `−0.0050` as `0.0050` on a conformance document. Every PDF-bound call must
 *   pass `ascii: true`; every DOM call must not. Same distinction, same reason,
 *   as `formatDeviation(mm, ascii)` in reportFormat.js.
 *
 *   `signed` — an explicit `+` on positives. TRUE ONLY FOR OFFSETS, which are
 *   deltas and read as `+0.0050 E`. A coordinate is a position, not a change:
 *   rendering it as `+589.1000` is a defect, not a nicety.
 *
 * @param {number} value Metre-scale value.
 * @param {{ascii?: boolean, signed?: boolean}} [options]
 * @returns {string} '—' when the value is absent or not finite.
 */
export function formatMetres(value, { ascii = false, signed = false } = {}) {
  if (!Number.isFinite(value)) return '—';
  const magnitude = Math.abs(value).toFixed(POSITION_DECIMALS);
  if (value < 0) return `${ascii ? '-' : '−'}${magnitude}`;
  return signed ? `+${magnitude}` : magnitude;
}

/**
 * UNSIGNED millimetre deviation, rendered to DEVIATION_DECIMALS.
 *
 * THE DIGITS OF A DEVIATION, IN ONE PLACE. `formatDelta` is this plus a sign;
 * the error-vector arrow labels use it bare, because an arrow already states its
 * sign by pointing and a `+` beside it is a second, redundant answer to a
 * question the geometry has settled.
 *
 * Split out so the two cannot disagree about DIGITS. They did: the marker label
 * rounded to whole millimetres while the results panel beside it rendered one
 * decimal, so a 4.9 mm deviation read as "5" on the model and "+4.9" in the
 * panel — one measurement, two numbers, on a conformance document.
 *
 * Guard is deliberately `NaN`-shaped rather than `isFinite`-shaped, matching
 * `formatDelta`'s historical behaviour exactly.
 */
export function formatDeviationMagnitude(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return Math.abs(value).toFixed(DEVIATION_DECIMALS);
}

/** Signed millimetre deviation, rendered to DEVIATION_DECIMALS. */
export function formatDelta(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const sign = value >= 0 ? '+' : '−';
  return `${sign}${formatDeviationMagnitude(value)}`;
}
